import "./index.scss";
import { FC, useState } from "react";
import { Button, Form, Input, Modal, Select, Table, message } from "antd";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "../../api/request";
import { EServerItemStatus, AdminUserServerItem, EServerItemRenewStatus } from "../../constant";
import { ColumnsType, TablePaginationConfig } from "antd/es/table";
import { CopyOutlined, EditOutlined } from "@ant-design/icons";
import copy from "copy-to-clipboard";

interface TableParams {
  pagination?: TablePaginationConfig;
}

const AdminUserServerManage: FC = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [isShowModal, setIsShowModal] = useState(false);

  const [status, setStatus] = useState<EServerItemStatus | "-1">("-1");
  const [searchContent, setSearchContent] = useState<string>("");
  const [searchParams, setSearchParams] = useState<{
    status: EServerItemStatus | -1;
    search_content: string;
  }>({
    status: -1,
    search_content: "",
  });

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 20,
    },
  });

  const { data, isFetching, isError } = useQuery({
    queryKey: ["admin-user-server-list", { page: tableParams.pagination?.current, page_size: tableParams.pagination?.pageSize, ...searchParams }],
    queryFn: async ({ queryKey }) => {
      const [_key, data] = queryKey;
      return request("manager/server-manager/", data);
    },
    placeholderData: keepPreviousData,
  });

  /** 编辑 */
  const updateServerMutation = useMutation({
    mutationFn: async (data: { res_id: number; remark: string }) => {
      return request("manager/update-server-detail/", data);
    },
    onSuccess: (data) => {
      //
    },
    onSettled: (data, error, variables, context) => {
      setIsShowModal(false);
      queryClient.invalidateQueries({ queryKey: ["admin-user-server-list"] });
    },
  });

  /** 完成续费 */
  const renewMutation = useMutation({
    mutationFn: async (data: { res_id: number }) => {
      return request("manager/server-renew/", data);
    },
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["admin-user-server-list"] });
    },
  });

  const columns: ColumnsType<AdminUserServerItem> = [
    {
      title: "vps服务",
      dataIndex: "res_name",
      key: "res_name",
    },
    {
      title: "订单编号",
      dataIndex: "order_id",
      key: "order_id",
    },
    {
      title: "使用情况",
      dataIndex: "status",
      key: "status",
      render: (status: number) => {
        return {
          [EServerItemStatus.PENDING]: "分配中",
          [EServerItemStatus.ACTIVE]: "正常",
          [EServerItemStatus.EXPIRED]: "过期",
          [EServerItemStatus.CANCELED]: "取消",
        }[status];
      },
    },
    {
      title: "到期时间",
      dataIndex: "res_renew_time",
      key: "res_renew_time",
    },
    {
      title: "来源",
      dataIndex: "res_type",
      key: "res_type",
    },
    {
      title: "购买账号",
      dataIndex: "user_email",
      key: "user_email",
    },
    {
      title: "用户续费时间",
      dataIndex: "last_renew_time",
      key: "last_renew_time",
    },
    {
      title: "操作续费",
      dataIndex: "renew_status",
      render(value, record, index) {
        if (value === EServerItemRenewStatus.IDLE) {
          return "否";
        }
        if (value === EServerItemRenewStatus.PENDING) {
          return (
            <Button
              type="primary"
              onClick={() => {
                renewMutation.mutate({ res_id: record.res_id });
              }}
            >
              完成续费
            </Button>
          );
        }
      },
    },
    {
      title: "操作",
      dataIndex: "res_id",
      key: "res_id",
      render(value: string, record) {
        return (
          <div className="tableOperation">
            <CopyOutlined
              onClick={() => {
                copy(`${record.res_user}:${record.res_password}@${record.res_ip}:${record.res_port}`);
                message.success('Copied!')
              }}
            />
            <EditOutlined
              onClick={() => {
                form.setFieldsValue(record);
                setIsShowModal(true);
              }}
            />
          </div>
        );
      },
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        ...pagination,
      },
    });
  };

  if (isError) {
    return <div>API Error</div>;
  }

  return (
    <main className="main">
      <div>Manage User Server</div>
      <div className="operationBar">
        <Select value={status} onChange={(value) => setStatus(value)} className="searchSelect">
          {Object.entries({
            "-1": "全部",
            [EServerItemStatus.ACTIVE]: "正常",
            [EServerItemStatus.EXPIRED]: "过期",
            [EServerItemStatus.PENDING]: "分配中",
            [EServerItemStatus.CANCELED]: "取消",
          }).map(([key, value]) => {
            return (
              <Select.Option key={key} value={key}>
                {value}
              </Select.Option>
            );
          })}
        </Select>
        <Input value={searchContent} onChange={(e) => setSearchContent(e.target.value)} className="searchInput" />
        <Button
          type="primary"
          onClick={() => {
            setTableParams({
              ...tableParams,
              pagination: {
                ...tableParams.pagination,
                current: 1,
              },
            });
            setSearchParams({
              status: Number(status),
              search_content: searchContent,
            });
          }}
        >
          搜索
        </Button>
      </div>
      <Table
        dataSource={data ? (data?.data?.data as AdminUserServerItem[]) : []}
        columns={columns}
        loading={isFetching}
        rowKey={"res_id"}
        pagination={{
          pageSize: data?.data.page_size,
          current: data?.data.page,
          total: data?.data.count,
          showTotal: (total) => `Total ${total} items`,
        }}
        onChange={handleTableChange}
      />
      <Modal
        title={"修改服务器"}
        open={isShowModal}
        onCancel={() => {
          form.resetFields();
          setIsShowModal(false);
        }}
        onOk={() => {
          const { user_email, ...values } = form.getFieldsValue();
          updateServerMutation.mutate(values);
        }}
        okButtonProps={{
          loading: updateServerMutation.isPending,
        }}
      >
        <Form form={form} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
          <Form.Item<AdminUserServerItem> label="购买账号" name="user_email">
            <Input disabled />
          </Form.Item>
          <Form.Item<AdminUserServerItem> label="ID" name="res_id">
            <Input disabled />
          </Form.Item>
          <Form.Item<AdminUserServerItem> label="IP" name="res_ip">
            <Input />
          </Form.Item>
          <Form.Item<AdminUserServerItem> label="Port" name="res_port">
            <Input />
          </Form.Item>
          <Form.Item<AdminUserServerItem> label="Username" name="res_user">
            <Input />
          </Form.Item>
          <Form.Item<AdminUserServerItem> label="Password" name="res_password">
            <Input />
          </Form.Item>
          <Form.Item<AdminUserServerItem> label="来源" name="res_type">
            <Input />
          </Form.Item>
          <Form.Item<AdminUserServerItem> label="来源账号" name="res_type_name">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </main>
  );
};

export default AdminUserServerManage;
